import { Country, usePhoneValidator } from '@/hooks/usePhoneValidator';
import { CaretDownIcon, WarningCircleIcon } from '@phosphor-icons/react';
import React, { useEffect, useRef, useState } from 'react';

interface PhoneInputProps extends React.ComponentProps<'input'> {
  disabled?: boolean;
  placeholder: string;
  helperText?: string;
}

export function PhoneInput({ disabled, placeholder, helperText, ...props }: PhoneInputProps) {
  const {
    isValid,
    error,
    selectedCountry,
    setSelectedCountry,
    validatePhone,
    formatPhone,
    countries,
  } = usePhoneValidator();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isDropdownOpen) {
      setSearchFilter('');
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isDropdownOpen]);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    validatePhone(inputValue, selectedCountry);
    props.onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = formatPhone(value, selectedCountry);
    setInputValue(formatted);

    if (!isValid && value) {
      validatePhone(value, selectedCountry);
    }

    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: formatted,
      },
    };

    props.onChange?.(syntheticEvent);
  };

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    setSearchFilter('');

    if (inputValue) {
      validatePhone(inputValue, country);
    }
  };

  const filteredCountries = countries.filter(
    (country) =>
      country.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
      country.code.toLowerCase().includes(searchFilter.toLowerCase()),
  );

  const getDialCode = (country: Country) => {
    return country.dialCodes?.[0] || '';
  };

  return (
    <div className="self-stretch flex flex-col justify-start items-start gap-1.5 relative">
      <div
        className={`self-stretch rounded-lg input-shadow outline-1 outline-offset-[-1px] inline-flex justify-start items-start overflow-hidden transition-colors ${
          disabled ? 'bg-gray-50' : 'bg-white'
        } ${
          !isValid
            ? 'input-error outline-error-300 focus-within:outline-error-300'
            : 'outline-gray-300 focus-within:outline-blue-300'
        }`}
      >
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          disabled={disabled}
          className="self-stretch pl-3.5 pr-3 py-2.5 inline-flex justify-between items-center overflow-hidden"
        >
          <span className="justify-start text-gray-900 text-base font-normal leading-normal">
            {selectedCountry.code}
          </span>
          <CaretDownIcon className="w-5 h-5 relative overflow-hidden text-gray-500" />
        </button>

        <div className="self-stretch pr-3.5 py-2.5 inline-flex justify-start items-center gap-2">
          <span className="flex-1 justify-start text-gray-500 text-base font-normal leading-normal">
            {getDialCode(selectedCountry)}
          </span>

          <input
            className="flex-1 justify-start placeholder:text-gray-500 text-gray-900 text-base font-normal leading-normal outline-none"
            placeholder={placeholder}
            type="tel"
            value={inputValue}
            onBlur={handleBlur}
            onChange={handleChange}
            disabled={disabled}
            {...props}
          />
          {!isValid && <WarningCircleIcon className="text-error-500 w-5 h-5" />}
        </div>
      </div>

      {/* Dropdown pegado al input */}
      {isDropdownOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] max-h-60 overflow-hidden mt-1"
        >
          <div className="p-2 border-b border-gray-200">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar país..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded outline-none focus:border-blue-300"
              autoFocus
            />
          </div>

          <div className="max-h-48 overflow-y-auto">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleCountrySelect(country)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg">{country.emoji}</span>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{country.name}</div>
                    <div className="text-xs text-gray-500">{getDialCode(country)}</div>
                  </div>
                  <span className="text-sm text-gray-400">{country.code}</span>
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">No se encontraron países</div>
            )}
          </div>
        </div>
      )}

      {error ? (
        <div className="self-stretch justify-start text-error-500 text-xs font-normal leading-none">
          {error}
        </div>
      ) : (
        <div className="self-stretch justify-start text-gray-600 text-xs font-normal leading-none">
          {helperText}
        </div>
      )}
    </div>
  );
}
